import { useEffect, useMemo, useState } from "react";
import { FlatList, Platform, View, type ViewStyle } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import {
  Icon,
  Skeleton,
  SkeletonGroup,
  SkeletonText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@visioni/a3s-ui/components";
import { Button, Text } from "@src/components";
import {
  buildSimilarFields,
  cameraName,
  formatTravelDirection,
  formatVehicleType,
  sightingAppearance,
  toVehicleLogRow,
  type SimilarFields,
  type CameraAssetGroup,
  type TimelineEntry,
  type VehicleDetection,
  type VehicleLogRow,
} from "@visioni/a3s-vehicle-tracker";
import { formatDateHeader, formatTimeOfDay, getDateKey } from "@visioni/a3s-vehicle-tracker/app";
import { CameraShots } from "./CameraShots";
import { isKnown } from "./card-utils";
import { VehicleClassIndicator } from "./VehicleClassIndicator";
import { VehicleDetailPanel } from "./VehicleDetailPanel";

/**
 * Column widths, shared by the header and every row so the columns line up.
 * The description takes what's left.
 */
const COLUMNS = {
  time: { width: 110 },
  shots: { width: 216 },
  direction: { width: 100 },
  rego: { width: 150 },
  vehicleClass: { width: 110 },
  vehicleType: { width: 150 },
  description: { flex: 1, minWidth: 160 },
  expand: { width: 48 },
} as const;

/** Below this the table scrolls sideways rather than squeezing the columns. */
const TABLE_MIN_WIDTH = 110 + 216 + 100 + 150 + 110 + 150 + 160 + 48;

const monoFontFamily = Platform.select({ ios: "Menlo", android: "monospace", default: "monospace" });

interface VehicleLogTableProps {
  entries: TimelineEntry[];
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  activeTimezone: string;
  /** Cameras response, for camera nicknames in the expanded rows. */
  cameras: readonly CameraAssetGroup[];
  onFindSimilar: (fields: SimilarFields) => void;
}

/** The vehicle log: one row per vehicle; "Load more" appends the next page. */
export function VehicleLogTable({
  entries,
  loading,
  hasMore,
  onLoadMore,
  activeTimezone,
  cameras,
  onFindSimilar,
}: VehicleLogTableProps) {
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const rows = useMemo(() => entries.map(toVehicleLogRow), [entries]);
  // FlatList only re-renders rows when `data` or `extraData` change, so
  // everything `renderItem` reads besides the row goes here. Without it a
  // pressed row stays collapsed and a timezone switch leaves stale times.
  const todayKey = useTodayKey(activeTimezone);
  const extraData = useMemo(
    () => ({ expandedKey, activeTimezone, todayKey, cameras, onFindSimilar }),
    [expandedKey, activeTimezone, todayKey, cameras, onFindSimilar],
  );

  return (
    <TableContainer horizontalScroll style={styles.container}>
      <Table minWidth={TABLE_MIN_WIDTH} accessibilityLabel="Vehicle log">
        <VehicleLogHeader />
        <TableBody>
          <FlatList
            data={rows}
            extraData={extraData}
            keyExtractor={(row) => row.key}
            style={Platform.OS === "web" ? webListStyle : styles.list}
            renderItem={({ item: row }) => (
              <VehicleRow
                row={row}
                activeTimezone={activeTimezone}
                todayKey={todayKey}
                cameras={cameras}
                expanded={row.key === expandedKey}
                onToggle={() => {
                  setExpandedKey((current) => (current === row.key ? null : row.key));
                }}
                onFindSimilar={onFindSimilar}
              />
            )}
            ListFooterComponent={
              hasMore ?
                <View style={styles.footer}>
                  <Button
                    variant="primary"
                    size="md"
                    onPress={onLoadMore}
                    disabled={loading}
                    label={loading ? "Loading..." : "Load more"}
                  />
                </View>
              : entries.length > 0 ?
                <View style={styles.footer}>
                  <Text textStyle="sm/regular" color="textMuted">
                    End of results
                  </Text>
                </View>
              : null
            }
          />
        </TableBody>
      </Table>
    </TableContainer>
  );
}

interface SightingHeaderProps {
  cameras: readonly CameraAssetGroup[];
  sighting: VehicleDetection;
  activeTimezone: string;
}

/**
 * Identifies one sighting in an expanded row: when and which camera, then the
 * plate it read and what the vehicle looked like. The detail panel below
 * doesn't show these, and a group's sightings are otherwise indistinguishable.
 */
function SightingHeader({ sighting, activeTimezone, cameras }: SightingHeaderProps) {
  const camera = cameraName(sighting.assetId, sighting.cameraId, cameras);
  const plate = isKnown(sighting.plateText) ? sighting.plateText : "No licence plate";
  const type = isKnown(sighting.vehicleType) ? formatVehicleType(sighting.vehicleType) : null;
  const details = [sightingAppearance(sighting), type, formatTravelDirection(sighting.travelDirection)].filter(
    (part) => part !== null,
  );

  return (
    <View>
      <Text textStyle="sm/semibold" color="text">
        {formatTimeOfDay(sighting.firstSeenAt, activeTimezone)}
        {camera ? ` · ${camera}` : ""}
      </Text>
      <Text textStyle="xs/regular" color="textMuted">
        <Text textStyle="xs/bold" color="textMuted" style={styles.rego}>
          {plate}
        </Text>
        {details.length > 0 ? ` · ${details.join(" · ")}` : ""}
      </Text>
    </View>
  );
}

/** Rows in the loading skeleton: one page (`getDefaultFilters().limit`). */
const SKELETON_ROWS = 12;
const SKELETON_KEYS = Array.from({ length: SKELETON_ROWS }, (_, index) => `skeleton-${index}`);

/** The vehicle log's layout with placeholder rows, shown while the first page loads. */
export function VehicleLogTableSkeleton() {
  return (
    <TableContainer horizontalScroll style={styles.container}>
      <Table minWidth={TABLE_MIN_WIDTH} accessibilityLabel="Loading vehicle log">
        <VehicleLogHeader />
        <TableBody>
          <SkeletonGroup>
            {SKELETON_KEYS.map((key) => (
              <TableRow key={key}>
                <TableCell {...COLUMNS.time}>
                  <SkeletonText lines={1} textStyle="sm/regular" width={64} />
                </TableCell>
                <TableCell {...COLUMNS.shots}>
                  <View style={styles.skeletonShots}>
                    <Skeleton variant="rounded" width={56} height={36} />
                    <Skeleton variant="rounded" width={56} height={36} />
                  </View>
                </TableCell>
                <TableCell {...COLUMNS.direction}>
                  <SkeletonText lines={1} textStyle="sm/regular" width={40} />
                </TableCell>
                <TableCell {...COLUMNS.rego}>
                  <SkeletonText lines={1} textStyle="sm/bold" width={72} />
                </TableCell>
                <TableCell {...COLUMNS.vehicleClass}>
                  <View style={styles.skeletonClass}>
                    <Skeleton variant="circular" width={14} height={14} />
                    <SkeletonText lines={1} textStyle="sm/regular" width={40} />
                  </View>
                </TableCell>
                <TableCell {...COLUMNS.vehicleType}>
                  <SkeletonText lines={1} textStyle="sm/regular" width={80} />
                </TableCell>
                <TableCell {...COLUMNS.description}>
                  <SkeletonText lines={1} textStyle="sm/regular" width={240} />
                </TableCell>
                <TableCell {...COLUMNS.expand} />
              </TableRow>
            ))}
          </SkeletonGroup>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function VehicleLogHeader() {
  return (
    <TableHead scrollbarGutter>
      <TableRow>
        <TableCell {...COLUMNS.time}>Time</TableCell>
        <TableCell {...COLUMNS.shots}>Camera shots</TableCell>
        <TableCell {...COLUMNS.direction}>Direction</TableCell>
        <TableCell {...COLUMNS.rego}>Licence Plate</TableCell>
        <TableCell {...COLUMNS.vehicleClass}>Class</TableCell>
        <TableCell {...COLUMNS.vehicleType}>Vehicle type</TableCell>
        <TableCell {...COLUMNS.description}>Description</TableCell>
        <TableCell {...COLUMNS.expand} />
      </TableRow>
    </TableHead>
  );
}

/**
 * Today's date key in `timeZone`, kept current across midnight so rows from
 * yesterday get their date line without waiting for another re-render. Checks
 * the clock once a minute; the state only changes when the day does.
 */
function useTodayKey(timeZone: string): string {
  const [todayKey, setTodayKey] = useState(() => getDateKey(new Date().toISOString(), timeZone));
  useEffect(() => {
    const update = () => {
      setTodayKey(getDateKey(new Date().toISOString(), timeZone));
    };
    update();
    const interval = setInterval(update, 60_000);
    return () => {
      clearInterval(interval);
    };
  }, [timeZone]);
  return todayKey;
}

interface VehicleRowProps {
  cameras: readonly CameraAssetGroup[];
  row: VehicleLogRow;
  /** Today's date key (`getDateKey`) in the active timezone. */
  todayKey: string;
  activeTimezone: string;
  expanded: boolean;
  onToggle: () => void;
  onFindSimilar: (fields: SimilarFields) => void;
}

function VehicleRow({ row, activeTimezone, todayKey, cameras, expanded, onToggle, onFindSimilar }: VehicleRowProps) {
  const time = formatTimeOfDay(row.time, activeTimezone);
  // Results can span several days, but the design has no date rows: say which
  // day only when it isn't today.
  const isToday = getDateKey(row.time, activeTimezone) === todayKey;
  const day = isToday ? null : formatDateHeader(row.time, activeTimezone);
  // Several sightings combine into one vehicle; say so, since the row only
  // shows one time and the shots column skips sightings without an image.
  const sightingCount = row.sightings.length > 1 ? `${row.sightings.length} sightings` : null;
  const timeNote = [day, sightingCount].filter((part) => part !== null).join(" · ");
  const { theme } = useUnistyles();

  return (
    <TableRow
      onPress={onToggle}
      expanded={expanded}
      accessibilityLabel={`${day === null ? "" : `${day} `}${time}, ${row.plates.length > 0 ? row.plates.join(" or ") : "no licence plate"}, ${row.vehicleType ?? "unknown type"}${row.directions.length > 0 ? `, ${row.directions.join(" or ")}` : ""}${sightingCount === null ? "" : `, ${sightingCount}`}`}
      expandedContent={
        <View style={styles.detail}>
          {row.sightings.map((sighting, index) => (
            <View key={sighting.id ?? `${sighting.createdAt}-${index}`} style={styles.sighting}>
              <SightingHeader sighting={sighting} activeTimezone={activeTimezone} cameras={cameras} />
              <VehicleDetailPanel vehicle={sighting} />
              <View style={styles.sightingActions}>
                <Button
                  variant="primary"
                  size="sm"
                  label="Find similar"
                  onPress={() => onFindSimilar(buildSimilarFields(sighting))}
                />
              </View>
            </View>
          ))}
        </View>
      }
    >
      <TableCell {...COLUMNS.time}>
        <Text textStyle="sm/regular" color="text">
          {time}
        </Text>
        {timeNote !== "" && (
          <Text textStyle="xs/regular" color="textMuted">
            {timeNote}
          </Text>
        )}
      </TableCell>
      <TableCell {...COLUMNS.shots}>
        <CameraShots shots={row.shots} />
      </TableCell>
      {/* A group's sightings can disagree (e.g. it entered then left): one line each, like plates. */}
      <TableCell {...COLUMNS.direction}>
        {row.directions.map((direction) => (
          <Text key={direction} textStyle="sm/regular" color="text" numberOfLines={1}>
            {direction}
          </Text>
        ))}
      </TableCell>
      {/* A vehicle can be read as more than one plate across cameras: one
          line each, so every reading is visible rather than cut off. */}
      <TableCell {...COLUMNS.rego}>
        {row.plates.length === 0 ?
          <Text textStyle="sm/regular" color="textMuted">
            No licence plate
          </Text>
        : row.plates.map((plate) => (
            <Text key={plate} textStyle="sm/bold" color="text" numberOfLines={1} style={styles.rego}>
              {plate}
            </Text>
          ))
        }
      </TableCell>
      <TableCell {...COLUMNS.vehicleClass}>
        <VehicleClassIndicator vehicleClass={row.vehicleClass} />
      </TableCell>
      <TableCell {...COLUMNS.vehicleType} noWrap>
        {row.vehicleType ?? "Not sure"}
      </TableCell>
      <TableCell {...COLUMNS.description} noWrap>
        {row.description}
      </TableCell>
      <TableCell {...COLUMNS.expand} align="center">
        <Icon name={expanded ? "chevronUp" : "chevronDown"} size={14} color={theme.colors.textMuted} />
      </TableCell>
    </TableRow>
  );
}

// The list's style on web: also reserves the scrollbar's width, matching
// `TableHead scrollbarGutter`, so the columns line up when a classic scrollbar
// shows. `scrollbar-gutter` isn't a native style, so native uses `styles.list`
// and this stays out of the unistyles sheet, which only types React Native styles.
const webListStyle = Object.assign({ flex: 1 } satisfies ViewStyle, { scrollbarGutter: "stable" });

const styles = StyleSheet.create((theme) => ({
  container: {
    flex: 1,
  },
  list: {
    flex: 1,
  },
  rego: {
    fontFamily: monoFontFamily,
  },
  // The expanded area sits straight under the row's cells, so it brings its
  // own top spacing.
  detail: {
    paddingTop: theme.spacing["3xl"],
    gap: theme.spacing["5xl"],
  },
  sighting: {
    gap: theme.spacing.md,
  },
  sightingActions: {
    flexDirection: "row",
  },
  skeletonShots: {
    flexDirection: "row",
    gap: theme.spacing.xs,
  },
  skeletonClass: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.sm,
  },
  footer: {
    alignItems: "center",
    paddingVertical: theme.spacing.xl,
  },
}));
